import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormGroup, Validators } from "@angular/forms";
import { MessagingService } from "../../core/messaging/messaging.service";
import { SeverityType } from "../../core/messaging/severity-type.enum";
import { FormService } from "../../dynamic-forms/form.service";
import { Dropdown } from "../../dynamic-forms/inputs/dropdown/dropdown.model";
import { NumeratorListItem } from "../../platform/api/numerator/numerator-list-item.model";
import { ProviderService } from "../../platform/api/provider/provider.service";
import { ProviderPacketItem } from "../../platform/modules/internal-pends/internal-pends-detail/internal-pends-detail-info/provider-packet-item.model";
import { CoverLetterTemplateService } from "../../platform/modules/retrieval/cover-letter-template/cover-letter-template.service";
import { ArrayHelper } from "../../utilities/contracts/array-helper";
import { NumberHelper } from "../../utilities/contracts/number-helper";
import { StringHelper } from "../../utilities/contracts/string-helper";
import { ModalComponent } from "../panel/modal/modal.component";

@Component({
  selector: "app-print-provider-packet-modal",
  templateUrl: "./print-provider-packet.component.html",
  styleUrls: ["./print-provider-packet.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrintProviderPacketComponent extends ModalComponent implements OnInit {
  @Input() providerPacketItem: ProviderPacketItem;
  @Input() gapType = "";
  // When true, the selection is queued as a background job resolved per-AID/provider server-side
  // (one file per AID), instead of being printed synchronously in this request.
  @Input() groupByAid = false;

  @Output() onPrintComplete = new EventEmitter<boolean>();
  @Output() onSent = new EventEmitter<void>();
  @Output() saving = new EventEmitter<void>();
  @Output() savingFailed = new EventEmitter<void>();

  numberChasesToProcess = 500;
  selectedCoverLetterTemplate: string;

  formGroup: FormGroup;
  coverLetterTemplateInput: Dropdown;

  constructor(
    private formService: FormService,
    private coverLetterTemplateService: CoverLetterTemplateService,
    private providerService: ProviderService,
    private messagingService: MessagingService,
    private changeDetector: ChangeDetectorRef
  ) { super(); }

  ngOnInit() {
    this.initializeForm();

    if (this.providerPacketItem == null) {
      this.providerPacketItem = {} as any;
    }
  }

  get totalChases(): number {
    return ArrayHelper.isAvailable(this.providerPacketItem.chaseIds) ? this.providerPacketItem.chaseIds.length : 0;
  }

  get isValid(): boolean {
    if (this.formGroup == null) {
      return false;
    }

    return this.formGroup.valid;
  }

  get showWarningMessage(): boolean {
    if (this.groupByAid) {
      return false;
    }
    return NumberHelper.isGreaterThan(this.totalChases, this.numberChasesToProcess, true);
  }

  get subHeader(): string {
    return StringHelper.isAvailable(this.gapType)
      ? this.gapType.toLowerCase() === "gap loaded"
        ? "TOTAL GAP CHASES"
        : "TOTAL GAP PURSUITS"
      : "TOTAL CHASES";
  }

  async printProviderPacket(): Promise<void> {
    this.onPrintComplete.emit();
    this.saving.emit();
    this.close();

    if (this.groupByAid) {
      await this.startPrintPacketJob();
      return;
    }

    const chaseIdsToProcess = ArrayHelper.chunkArray(this.providerPacketItem.chaseIds, this.numberChasesToProcess);
    const chaseNumeratorsToProcess = ArrayHelper.chunkArray(this.providerPacketItem.chaseNumeratorList, this.numberChasesToProcess);
    const results = await Promise.all(chaseIdsToProcess.map((item, index) => this.sendPacketRequest(item, chaseNumeratorsToProcess[index])));
    const allSucceeded = results.every(result => result);

    if (!allSucceeded) {
      this.savingFailed.emit();
    }
  }

  private async startPrintPacketJob(): Promise<void> {
    const providerPacketItem: ProviderPacketItem = { ...this.providerPacketItem, templateName: this.selectedCoverLetterTemplate };
    try {
      await this.providerService.startPrintPacketJob(providerPacketItem).toPromise();
      this.messagingService.showToast(
        "Print packet request queued. You'll be notified when it's ready for download.", SeverityType.SUCCESS);
      this.onSent.emit();
    } catch (err) {
      this.savingFailed.emit();
      this.messagingService.showToast("Unable to submit the print packet request. Please try again.", SeverityType.ERROR);
    }
  }

  private async sendPacketRequest(chaseIds: number[], chaseNumeratorList: NumeratorListItem[]): Promise<boolean> {
    const providerPacketItem: ProviderPacketItem = { ...this.providerPacketItem, chaseIds, templateName: this.selectedCoverLetterTemplate,
                                                     chaseNumeratorList };
    try {
      const data = await this.providerService.printProviderPacket(providerPacketItem).toPromise();
      if (data != null) {
        this.providerService.downloadPdf(data);
        this.formService.updateDom.next();
      } else {
        this.messagingService.showToast("No provider packet available for download.", SeverityType.INFO);
      }
      return true;
    } catch (err) {
      let message = "An error occurred while downloading the provider packet.";
      if (err.status === 404) {
        if (err.error instanceof ArrayBuffer) {
          // Convert ArrayBuffer to a string
          const decoder = new TextDecoder("utf-8");
          const jsonString = decoder.decode(err.error);
          const json = JSON.parse(jsonString);
          message = json.message;
        } else if (typeof err.error === "string") {
          message = err.error;
        } else if (err.error?.message) {
          message = err.error.message;
        }
      }
      this.messagingService.showToast(message, SeverityType.ERROR);
      return false;
    }
  }

  open(): void {
    this.getCoverLetterTemplateList();
    this.changeDetector.markForCheck();
  }

  close(): void {
    this.visible = false;
    this.onHide.emit();
    this.formGroup.reset();
    this.changeDetector.markForCheck();
  }

  onVisibleChange(visible: boolean): void {
    this.visibleChange.emit(visible);
  }

  onCoverLetterChange(event) {
    this.selectedCoverLetterTemplate = event.value;
  }

  private initializeForm(): void {
    this.coverLetterTemplateInput = new Dropdown({
      key: "coverLetterTemplate",
      label: "SELECT TEMPLATE",
      placeholder: "Select Cover Letter Body",
      validators: Validators.required,
      errorMessages: {
        required: "Cover letter template is required.",
      },
      appendTo: "body",
    });

    this.formGroup = this.formService.createFormGroup([this.coverLetterTemplateInput]);
  }

  private getCoverLetterTemplateList(): void {
    this.coverLetterTemplateService.getCoverLetterTemplate(this.gapType)
      .subscribe({
        next: templatelist => {
          this.coverLetterTemplateInput = new Dropdown({ ...this.coverLetterTemplateInput, options: templatelist } as any);
          this.changeDetector.markForCheck();
        },
        error: () => {
          this.messagingService.showToast("Unable to load cover letter templates. Please try again.", SeverityType.ERROR);
          this.changeDetector.markForCheck();
        },
      });
  }
}
